import React, { JSX, useCallback, useId, useState } from 'react';
import {
  CustomInput,
  MEDIAQUERY_DEFAULTS,
  useConfig,
  useMediaQuery,
  useTranslation
} from '@ibe/components';
import { Tooltip } from 'reactstrap';
import { PromotionCodeData } from './PromotionCode';
import { IconDefinition } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sanitize } from '@ibe/services';
import { MINI_GROUP, MINI_GROUP_IMG, SMALL_GROUP, SMALL_GROUP_IMG } from '../Util/constant';
import fallback from '../Translations/generated/product-ibe.de.json';
import useThgConfig from '../Hooks/useThgConfig';
import Keys from '../Translations/generated/product-ibe.de.json.keys';

const SingleSelection = ({
  groupSizeChecked,
  setIsChecked,
  groupSize,
  showInfoTextInFront,
  checkboxLabel,
  icon,
  promotionCode,
  selectionId,
  showTooltip,
  tooltipText
}: {
  groupSizeChecked: boolean;
  setIsChecked: (isChecked: boolean) => void;
  icon?: IconDefinition;
  groupSize?: string;
  showInfoTextInFront?: boolean;
  checkboxLabel?: string;
  promotionCode: PromotionCodeData;
  selectionId: string;
  showTooltip?: boolean;
  tooltipText?: string;
}): JSX.Element => {
  const handleChange = useCallback((): void => {
    setIsChecked(!groupSizeChecked);
  }, [groupSizeChecked, setIsChecked, promotionCode]);
  const { t } = useTranslation('product-ibe', fallback);
  const config = useConfig();
  const thgConfig = useThgConfig();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const tooltipTextContent = tooltipText ?? '';
  const isSmall = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });

  const imgBaseUrl = thgConfig.baseUrl ?? config.translationsUrl;
  let infoText = undefined;
  if (groupSize === SMALL_GROUP) {
    infoText = t(Keys.smallGroupInfo);
  } else if (groupSize === MINI_GROUP) {
    infoText = t(Keys.miniGroupInfo);
  }

  const defaultCheckBoxLabel =
    checkboxLabel ??
    (!checkboxLabel && groupSize === SMALL_GROUP
      ? t(Keys.smallGroupLabel)
      : t(Keys.miniGroupLabel));
  let imgSrc = undefined;
  if (groupSize === SMALL_GROUP) {
    imgSrc = `${imgBaseUrl}` + SMALL_GROUP_IMG;
  } else if (groupSize === MINI_GROUP) {
    imgSrc = `${imgBaseUrl}` + MINI_GROUP_IMG;
  }

  const plainInfoText = infoText ? infoText.replace(/<\/?[\w\s="'./:;#-]+>/gi, '') : '';
  const uniqueId = useId();

  return (
    <section
      aria-label={`${defaultCheckBoxLabel} options`}
      id={`${selectionId}-region`}
      className={`group-size-selection ${groupSize}`}
    >
      {showInfoTextInFront && (
        <div className="group-size-selection__info">
          {imgSrc && <img src={imgSrc} alt={defaultCheckBoxLabel} />}
          {infoText && (
            <span
              dangerouslySetInnerHTML={{ __html: sanitize(infoText) }}
              aria-hidden="true"
            ></span>
          )}
          <span id={uniqueId} className="sr-only">
            {plainInfoText}
          </span>
        </div>
      )}
      <CustomInput
        id={selectionId}
        type="checkbox"
        checked={groupSizeChecked}
        onChange={handleChange}
        label={defaultCheckBoxLabel}
      />
      {!showInfoTextInFront && (
        <div id={selectionId + '_cont'} className="group-size-selection__info__end">
          {imgSrc ? (
            <img src={imgSrc} alt={defaultCheckBoxLabel} />
          ) : (
            !!icon && (
              <>
                <FontAwesomeIcon
                  id={selectionId + '_tooltip'}
                  icon={icon}
                  onClick={toggle}
                ></FontAwesomeIcon>
                {showTooltip && (
                  <Tooltip
                    placement={'right'}
                    isOpen={tooltipOpen}
                    target={selectionId + '_tooltip'}
                    toggle={toggle}
                    popperClassName="tooltip-popper"
                    innerClassName="tooltip-inner"
                    container={!isSmall ? undefined : selectionId + '_cont'}
                    onClick={toggle}
                    autohide={true}
                  >
                    <div className="tooltip__text">{''}</div>
                    <div
                      className="tooltip__info"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(tooltipTextContent)
                      }}
                    />
                  </Tooltip>
                )}
              </>
            )
          )}
        </div>
      )}
    </section>
  );
};

export default SingleSelection;
